import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { 
  Home, FileText, DollarSign, Users, Plus, 
  LogOut, User, ChevronRight, AlertCircle, CheckCircle2, Download, FolderOpen,
  CreditCard, Calendar, Bell, Edit2, Trash2, TrendingUp, BarChart3, Activity, X, Gift,
  Wallet, ArrowUpRight, ArrowDownLeft, Banknote, Eye, ExternalLink, HelpCircle, BookOpen, Headphones, RefreshCw
} from "lucide-react";
import { NestIcon } from "../components/NestIcon";
import { NotificationBell } from "../components/NotificationBell";
import { UserAvatarMenu } from "../components/UserAvatarMenu";
import { OnboardingTour, TourHelpButton } from "../components/OnboardingTour";
import { PreferencesDialog } from "../components/Preferences";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    quotes: [],
    policies: [],
    claims: [],
    contracts: []
  });
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  
  // Quote edit/delete state
  const [editingQuote, setEditingQuote] = useState(null);
  const [editForm, setEditForm] = useState({
    monthly_rent: "",
    lease_start_date: "",
    lease_end_date: "",
    employer_name: "",
    employment_start_date: "",
    annual_income: ""
  });
  const [savingQuote, setSavingQuote] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Wallet state
  const [wallet, setWallet] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    bank_name: "",
    account_number: "",
    routing_number: "",
    account_holder_name: "",
    notes: ""
  });
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  
  // Stripe Connect state
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  
  // Loyalty reward state
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  
  // Data table modal state
  const [activeModal, setActiveModal] = useState(null); // 'quotes', 'policies', 'claims', 'coverage'

  // Flexible payment state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPolicyForPayment, setSelectedPolicyForPayment] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [monthsToPay, setMonthsToPay] = useState(1);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Payment history state
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Subscription state
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [creatingSubscription, setCreatingSubscription] = useState(false);

  useEffect(() => {
    // Check for payment status
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    const fullYear = searchParams.get("full_year");
    
    // Check for subscription status
    const subscriptionStatus = searchParams.get("subscription");
    const subscriptionSessionId = searchParams.get("session_id");

    if (subscriptionStatus === "success" && subscriptionSessionId) {
      checkSubscriptionStatus(subscriptionSessionId);
      navigate("/dashboard", { replace: true });
    }

    if (paymentStatus === "success" && sessionId) {
      if (fullYear === "true") {
        toast.success("Full year premium paid! You are now eligible for claims.");
      }
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment was cancelled");
    }

    // Check for Stripe Connect return
    const connectStatus = searchParams.get("connect");
    if (connectStatus === "complete") {
      toast.success("Payout account setup complete! You can now receive direct bank payouts.");
      navigate("/dashboard", { replace: true });
    } else if (connectStatus === "retry") {
      toast.info("Please complete your payout account setup to receive direct payments.");
      navigate("/dashboard", { replace: true });
    }

    fetchDashboardData();
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info("Payment is being processed. Please check back shortly.");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Your policy is now active.");
        fetchDashboardData();
        // Clear query params
        navigate("/dashboard", { replace: true });
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Payment status check error:", error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [quotesRes, policiesRes, claimsRes, contractsRes, analyticsRes, walletRes, withdrawalsRes, loyaltyRes, paymentHistoryRes, subscriptionsRes, connectRes] = await Promise.all([
        axios.get(`${API}/quotes/`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/policies/`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/claims/`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contracts/`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/users/analytics/overview`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/users/wallet`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/users/withdrawals`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/users/loyalty/status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/payments/premium-history`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/payments/subscription/active`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/payouts/connect-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);

      setData({
        quotes: quotesRes.data,
        policies: policiesRes.data,
        claims: claimsRes.data,
        contracts: contractsRes.data
      });
      setAnalytics(analyticsRes.data);
      setWallet(walletRes.data);
      setWithdrawals(withdrawalsRes.data);
      setLoyaltyStatus(loyaltyRes.data);
      setPaymentHistory(paymentHistoryRes.data);
      setActiveSubscriptions(subscriptionsRes.data || []);
      setConnectStatus(connectRes.data);
    } catch (error) {
      console.error("Dashboard data error:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Quote Edit/Delete handlers
  const handleEditQuote = (quote) => {
    setEditingQuote(quote);
    setEditForm({
      monthly_rent: quote.monthly_rent?.toString() || "",
      lease_start_date: quote.lease_start_date || "",
      lease_end_date: quote.lease_end_date || "",
      employer_name: quote.employer_name || "",
      employment_start_date: quote.employment_start_date || "",
      annual_income: quote.annual_income?.toString() || ""
    });
  };

  const handleSaveQuote = async () => {
    if (!editingQuote) return;
    
    setSavingQuote(true);
    try {
      await axios.put(`${API}/quotes/${editingQuote.quote_id}`, {
        monthly_rent: parseFloat(editForm.monthly_rent),
        lease_start_date: editForm.lease_start_date,
        lease_end_date: editForm.lease_end_date,
        employer_name: editForm.employer_name,
        employment_start_date: editForm.employment_start_date,
        annual_income: parseFloat(editForm.annual_income)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Quote updated successfully");
      setEditingQuote(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update quote");
    } finally {
      setSavingQuote(false);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    try {
      await axios.delete(`${API}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Quote deleted successfully");
      setDeleteConfirm(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete quote");
    }
  };

  const handlePayQuote = async (quoteId) => {
    try {
      const response = await axios.post(`${API}/payments/checkout/session`, {
        quote_id: quoteId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate payment");
    }
  };

  const handleDownloadContract = async (policyId) => {
    try {
      const response = await axios.get(`${API}/policies/${policyId}/contract/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TheRentPayer_Contract_${policyId}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Contract downloaded");
    } catch (error) {
      toast.error("Failed to download contract");
    }
  };

  const handlePayPremium = async (policyId) => {
    // Open flexible payment dialog
    setSelectedPolicyForPayment(data.policies.find(p => p.policy_id === policyId));
    await fetchPaymentStatus(policyId);
    setPaymentDialogOpen(true);
  };

  const fetchPaymentStatus = async (policyId) => {
    try {
      const response = await axios.get(`${API}/users/policies/${policyId}/payment-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch payment status:", error);
    }
  };

  const handleFlexiblePayment = async () => {
    if (!selectedPolicyForPayment) return;
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/users/policies/${selectedPolicyForPayment.policy_id}/pay-premium`, {
        policy_id: selectedPolicyForPayment.policy_id,
        months_to_pay: monthsToPay,
        payment_method: "card"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success("Payment initiated");
        setPaymentDialogOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePayFullYear = async () => {
    if (!selectedPolicyForPayment) return;
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/users/policies/${selectedPolicyForPayment.policy_id}/pay-full-year`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.remaining_amount === 0) {
        toast.success("Full annual premium already paid! You are eligible for claims.");
        setPaymentDialogOpen(false);
        fetchDashboardData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleExportPaymentHistoryPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/payments/premium-history/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const contentType = response.headers['content-type'];
      const isPdf = contentType?.includes('pdf');
      const extension = isPdf ? 'pdf' : 'html';
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TheRentPayer_Payment_History.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Payment history exported successfully!");
    } catch (error) {
      toast.error("Failed to export payment history");
      console.error("Export error:", error);
    } finally {
      setExportingPdf(false);
    }
  };

  const checkSubscriptionStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/subscription/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.subscription_status === "active") {
        toast.success("Subscription activated! Your premium will be automatically charged each month.");
        fetchDashboardData();
        fetchActiveSubscriptions();
      }
    } catch (error) {
      console.error("Subscription status check error:", error);
    }
  };

  const fetchActiveSubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/payments/subscription/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveSubscriptions(response.data || []);
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
    }
  };

  const handleSetupSubscription = async (policyId) => {
    setCreatingSubscription(true);
    try {
      const response = await axios.post(`${API}/payments/subscription/create`, {
        policy_id: policyId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to setup subscription");
    } finally {
      setCreatingSubscription(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm("Are you sure you want to cancel your subscription? Your coverage will continue until the end of the current billing period.")) {
      return;
    }
    
    try {
      await axios.post(`${API}/payments/subscription/cancel/${subscriptionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscription will be cancelled at end of billing period");
      fetchActiveSubscriptions();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    }
  };

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    if (!withdrawForm.amount || parseFloat(withdrawForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (parseFloat(withdrawForm.amount) > (wallet?.available_balance || 0)) {
      toast.error(`Amount exceeds available balance ($${wallet?.available_balance?.toFixed(2)})`);
      return;
    }
    if (!withdrawForm.bank_name || !withdrawForm.account_number || !withdrawForm.routing_number || !withdrawForm.account_holder_name) {
      toast.error("Please fill in all bank details");
      return;
    }

    setSubmittingWithdrawal(true);
    try {
      await axios.post(`${API}/users/withdrawals`, {
        amount: parseFloat(withdrawForm.amount),
        bank_name: withdrawForm.bank_name,
        account_number: withdrawForm.account_number,
        routing_number: withdrawForm.routing_number,
        account_holder_name: withdrawForm.account_holder_name,
        notes: withdrawForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Withdrawal request submitted! We'll process it within 2-3 business days.");
      setIsWithdrawOpen(false);
      setWithdrawForm({
        amount: "",
        bank_name: "",
        account_number: "",
        routing_number: "",
        account_holder_name: "",
        notes: ""
      });
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit withdrawal request");
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const getWithdrawalStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-blue-100 text-blue-800",
      paid: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const handleConnectOnboarding = async () => {
    setConnectLoading(true);
    try {
      const response = await axios.post(`${API}/payouts/connect-onboarding`, 
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start payout account setup");
    } finally {
      setConnectLoading(false);
    }
  };

  const isPremiumDue = (policy) => {
    if (!policy.next_billing_date) return false;
    const billingDate = new Date(policy.next_billing_date);
    return billingDate <= new Date();
  };

  const getDaysUntilBilling = (policy) => {
    if (!policy.next_billing_date) return null;
    const billingDate = new Date(policy.next_billing_date);
    const today = new Date();
    const diffTime = billingDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      active: "bg-blue-100 text-blue-800",
      under_review: "bg-purple-100 text-purple-800",
      paid: "bg-emerald-100 text-emerald-800",
      expired: "bg-gray-100 text-gray-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const activePolicy = data.policies.find(p => p.status === "active");

  return (
    <div className="min-h-screen bg-background">
      {/* Onboarding Tour for Users */}
      <OnboardingTour userType="user" />

      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <NestIcon className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                TheRentPayer
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link active" data-tour="dashboard">Dashboard</Link>
              <Link to="/quote" className="nav-link" data-tour="get-quote">Get Quote</Link>
              <Link to="/documents" className="nav-link" data-tour="policies">Documents</Link>
              <Link to="/beneficiaries" className="nav-link">Beneficiaries</Link>
              <Link to="/referrals" className="nav-link" data-tour="referrals">Referrals</Link>
            </nav>

            <div className="flex items-center gap-3" data-tour="profile">
              <PreferencesDialog />
              <TourHelpButton userType="user" />
              <NotificationBell token={token} />
              <UserAvatarMenu user={user} token={token} onLogout={logout} />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="user-dashboard">
        {/* Welcome Section */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
              Welcome back, {user?.full_name?.split(' ')[0] || 'there'}!
            </h1>
            <p className="text-muted-foreground">
              Manage your rent protection coverage and claims
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center gap-2"
            data-testid="toggle-analytics-btn"
          >
            <BarChart3 className="h-4 w-4" />
            {showAnalytics ? "Hide Analytics" : "View Analytics"}
          </Button>
        </div>

        {/* Analytics Section - Expandable */}
        {showAnalytics && analytics && (
          <div className="mb-8 animate-in slide-in-from-top duration-300">
            <Card className="card-organic bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Young Serif, serif' }}>
                  <Activity className="h-5 w-5 text-primary" />
                  Your Analytics Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/80 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-primary">${analytics.payments?.total_premiums_paid?.toLocaleString() || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Premiums Paid</p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-600">${analytics.policies?.total_coverage?.toLocaleString() || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Coverage</p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-blue-600">${analytics.claims?.total_claimed?.toLocaleString() || 0}</p>
                    <p className="text-sm text-muted-foreground">Claims Received</p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-amber-600">{analytics.beneficiaries || 0}</p>
                    <p className="text-sm text-muted-foreground">Beneficiaries</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  {/* Quotes Breakdown */}
                  <div className="bg-white/80 p-4 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" /> Quotes
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Pending</span>
                        <span className="font-medium text-yellow-600">{analytics.quotes?.pending || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Approved</span>
                        <span className="font-medium text-green-600">{analytics.quotes?.approved || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Converted</span>
                        <span className="font-medium text-blue-600">{analytics.quotes?.converted || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Policies Breakdown */}
                  <div className="bg-white/80 p-4 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <NestIcon className="h-4 w-4 text-blue-600" /> Policies
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total</span>
                        <span className="font-medium">{analytics.policies?.total || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Active</span>
                        <span className="font-medium text-green-600">{analytics.policies?.active || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Claims Breakdown */}
                  <div className="bg-white/80 p-4 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600" /> Claims
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Pending</span>
                        <span className="font-medium text-yellow-600">{analytics.claims?.pending || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Approved</span>
                        <span className="font-medium text-green-600">{analytics.claims?.approved || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Paid</span>
                        <span className="font-medium text-emerald-600">{analytics.claims?.paid || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Stats - Clickable Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card 
            className="dashboard-card cursor-pointer hover:border-primary hover:shadow-md transition-all"
            onClick={() => setActiveModal('quotes')}
            data-testid="stat-quotes"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{data.quotes.length}</p>
                  <p className="text-sm text-muted-foreground">Quotes</p>
                </div>
                <Eye className="h-4 w-4 text-muted-foreground ml-auto" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="dashboard-card cursor-pointer hover:border-primary hover:shadow-md transition-all"
            onClick={() => setActiveModal('policies')}
            data-testid="stat-policies"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <NestIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{data.policies.length}</p>
                  <p className="text-sm text-muted-foreground">Policies</p>
                </div>
                <Eye className="h-4 w-4 text-muted-foreground ml-auto" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="dashboard-card cursor-pointer hover:border-primary hover:shadow-md transition-all"
            onClick={() => setActiveModal('claims')}
            data-testid="stat-claims"
            data-tour="claims"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="bg-amber-100 p-2 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{data.claims.length}</p>
                  <p className="text-sm text-muted-foreground">Claims</p>
                </div>
                <Eye className="h-4 w-4 text-muted-foreground ml-auto" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="dashboard-card cursor-pointer hover:border-primary hover:shadow-md transition-all"
            onClick={() => setActiveModal('coverage')}
            data-testid="stat-coverage"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {activePolicy ? "Active" : "None"}
                  </p>
                  <p className="text-sm text-muted-foreground">Coverage</p>
                </div>
                <Eye className="h-4 w-4 text-muted-foreground ml-auto" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Table Modals */}
        {/* Quotes Modal */}
        <Dialog open={activeModal === 'quotes'} onOpenChange={(open) => !open && setActiveModal(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Young Serif, serif' }} className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                All Quotes ({data.quotes.length})
              </DialogTitle>
            </DialogHeader>
            <div className="pt-4">
              {data.quotes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No quotes yet</p>
                  <Link to="/quote">
                    <Button className="mt-4 btn-primary">
                      <Plus className="h-4 w-4 mr-2" />
                      Get Your First Quote
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-3 font-medium">Quote ID</th>
                        <th className="text-left p-3 font-medium">Monthly Rent</th>
                        <th className="text-left p-3 font-medium">Premium</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Created</th>
                        <th className="text-left p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.quotes.map((quote) => (
                        <tr key={quote.quote_id} className="border-b hover:bg-muted/30">
                          <td className="p-3">
                            <code className="text-xs bg-muted px-2 py-1 rounded">{quote.quote_id}</code>
                          </td>
                          <td className="p-3 font-medium">${quote.monthly_rent?.toLocaleString()}</td>
                          <td className="p-3">${Number(quote.monthly_premium).toFixed(2)}/mo</td>
                          <td className="p-3">
                            <Badge className={getStatusBadge(quote.status)}>{quote.status}</Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {new Date(quote.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              {(quote.status === "pending" || quote.status === "approved") && (
                                <Button size="sm" onClick={() => handlePayQuote(quote.quote_id)}>
                                  Pay ${Number(quote.total_first_payment).toFixed(2)}
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => { setActiveModal(null); setEditingQuote(quote); }}>
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => { setActiveModal(null); setDeleteConfirm(quote.quote_id); }}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Policies Modal */}
        <Dialog open={activeModal === 'policies'} onOpenChange={(open) => !open && setActiveModal(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Young Serif, serif' }} className="flex items-center gap-2">
                <NestIcon className="h-5 w-5 text-blue-600" />
                All Policies ({data.policies.length})
              </DialogTitle>
            </DialogHeader>
            <div className="pt-4">
              {data.policies.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <NestIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No policies yet</p>
                  <p className="text-sm mt-2">Get a quote and make a payment to activate your coverage</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-3 font-medium">Policy ID</th>
                        <th className="text-left p-3 font-medium">Monthly Rent</th>
                        <th className="text-left p-3 font-medium">Premium</th>
                        <th className="text-left p-3 font-medium">Coverage Period</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.policies.map((policy) => (
                        <tr key={policy.policy_id} className="border-b hover:bg-muted/30">
                          <td className="p-3">
                            <code className="text-xs bg-muted px-2 py-1 rounded">{policy.policy_id}</code>
                          </td>
                          <td className="p-3 font-medium">${policy.monthly_rent?.toLocaleString()}</td>
                          <td className="p-3">${Number(policy.monthly_premium).toFixed(2)}/mo</td>
                          <td className="p-3 text-sm">
                            {policy.coverage_start_date ? new Date(policy.coverage_start_date).toLocaleDateString() : 
                             policy.start_date ? new Date(policy.start_date).toLocaleDateString() : 'N/A'} - {' '}
                            {policy.coverage_end_date ? new Date(policy.coverage_end_date).toLocaleDateString() : 
                             policy.end_date ? new Date(policy.end_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="p-3">
                            <Badge className={getStatusBadge(policy.status)}>{policy.status}</Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleDownloadContract(policy.policy_id)}>
                                <Download className="h-3 w-3 mr-1" />
                                Contract
                              </Button>
                              {policy.status === 'active' && (
                                <Link to="/file-claim">
                                  <Button size="sm" variant="secondary">
                                    File Claim
                                  </Button>
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Claims Modal */}
        <Dialog open={activeModal === 'claims'} onOpenChange={(open) => !open && setActiveModal(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Young Serif, serif' }} className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                All Claims ({data.claims.length})
              </DialogTitle>
            </DialogHeader>
            <div className="pt-4">
              {data.claims.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No claims filed yet</p>
                  <p className="text-sm mt-2">We hope you never need to use this!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-3 font-medium">Claim ID</th>
                        <th className="text-left p-3 font-medium">Policy</th>
                        <th className="text-left p-3 font-medium">Job Loss Date</th>
                        <th className="text-left p-3 font-medium">Reason</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Filed On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.claims.map((claim) => (
                        <tr key={claim.claim_id} className="border-b hover:bg-muted/30">
                          <td className="p-3">
                            <code className="text-xs bg-muted px-2 py-1 rounded">{claim.claim_id}</code>
                          </td>
                          <td className="p-3 text-sm">{claim.policy_id}</td>
                          <td className="p-3 text-sm">
                            {claim.job_loss_date ? new Date(claim.job_loss_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="p-3 text-sm max-w-[200px] truncate">
                            {claim.reason_for_job_loss || 'N/A'}
                          </td>
                          <td className="p-3">
                            <Badge className={getStatusBadge(claim.status)}>{claim.status}</Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {new Date(claim.filed_at || claim.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Coverage Details Modal */}
        <Dialog open={activeModal === 'coverage'} onOpenChange={(open) => !open && setActiveModal(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Young Serif, serif' }} className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Coverage Details
              </DialogTitle>
            </DialogHeader>
            <div className="pt-4">
              {!activePolicy ? (
                <div className="text-center py-8">
                  <NestIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-semibold mb-2">No Active Coverage</h3>
                  <p className="text-muted-foreground mb-4">
                    Get protected today! Our rent insurance covers up to 3 months of rent if you lose your job.
                  </p>
                  <Link to="/quote">
                    <Button className="btn-primary">
                      <Plus className="h-4 w-4 mr-2" />
                      Get a Quote Now
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <Badge className={`${getStatusBadge(activePolicy.status)} text-sm px-3 py-1`}>
                      {activePolicy.status === 'active' ? '✓ Active Coverage' : activePolicy.status}
                    </Badge>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadContract(activePolicy.policy_id)}>
                      <Download className="h-4 w-4 mr-2" />
                      Download Contract
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Policy Number</p>
                      <p className="font-mono text-sm font-medium">{activePolicy.policy_id}</p>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Monthly Rent Covered</p>
                      <p className="text-xl font-bold text-primary">${activePolicy.monthly_rent?.toLocaleString()}</p>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Monthly Premium</p>
                      <p className="text-lg font-semibold">${Number(activePolicy.monthly_premium).toFixed(2)}</p>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">Maximum Claim</p>
                      <p className="text-lg font-semibold">${(activePolicy.monthly_rent * 3)?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">(3 months coverage)</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Coverage Period</h4>
                    <p className="text-blue-700">
                      {activePolicy.coverage_start_date ? new Date(activePolicy.coverage_start_date).toLocaleDateString() : 
                       activePolicy.start_date ? new Date(activePolicy.start_date).toLocaleDateString() : 'N/A'} 
                      {' '} to {' '}
                      {activePolicy.coverage_end_date ? new Date(activePolicy.coverage_end_date).toLocaleDateString() : 
                       activePolicy.end_date ? new Date(activePolicy.end_date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-800 mb-2">What's Covered</h4>
                    <ul className="text-sm text-amber-700 space-y-1">
                      <li>✓ Involuntary job loss due to layoffs or downsizing</li>
                      <li>✓ Position elimination due to economic conditions</li>
                      <li>✓ Up to 3 months of rent payments</li>
                    </ul>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Link to="/file-claim" className="flex-1">
                      <Button className="w-full" variant="outline">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        File a Claim
                      </Button>
                    </Link>
                    <Link to="/documents" className="flex-1">
                      <Button className="w-full" variant="outline">
                        <FolderOpen className="h-4 w-4 mr-2" />
                        View Documents
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Wallet Section */}
        {wallet && (
          <Card className="card-organic mb-8 bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200" data-testid="wallet-section" data-tour="wallet">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="bg-emerald-100 p-3 rounded-xl">
                    <Wallet className="h-8 w-8 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-700">Your Wallet Balance</p>
                    <p className="text-3xl font-bold text-emerald-800" data-testid="wallet-balance">
                      ${wallet.wallet_balance?.toFixed(2) || "0.00"}
                    </p>
                    {wallet.pending_withdrawals > 0 && (
                      <p className="text-xs text-emerald-600">
                        Available: ${wallet.available_balance?.toFixed(2)} (${wallet.pending_withdrawals?.toFixed(2)} pending)
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-3">
                  {wallet.transactions?.length > 0 && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                          <ArrowDownLeft className="h-4 w-4 mr-2" />
                          History
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg max-h-[70vh] overflow-auto">
                        <DialogHeader>
                          <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>Transaction History</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 pt-4">
                          {wallet.transactions.map((txn, idx) => (
                            <div key={txn.transaction_id || idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${txn.type === 'credit' ? 'bg-green-100' : 'bg-red-100'}`}>
                                  {txn.type === 'credit' ? (
                                    <ArrowDownLeft className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <ArrowUpRight className="h-4 w-4 text-red-600" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{txn.description}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(txn.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className={`font-semibold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                                {txn.type === 'credit' ? '+' : '-'}${txn.amount?.toFixed(2)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  <Dialog open={isWithdrawOpen} onOpenChange={setIsWithdrawOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        disabled={!wallet.available_balance || wallet.available_balance <= 0}
                        data-testid="withdraw-btn"
                      >
                        <Banknote className="h-4 w-4 mr-2" />
                        Withdraw
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
                          <Banknote className="inline h-5 w-5 mr-2 text-emerald-600" />
                          Withdraw Funds
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleWithdrawal} className="space-y-4 pt-4">
                        <div className="bg-emerald-50 p-4 rounded-lg text-center">
                          <p className="text-sm text-emerald-700">Available Balance</p>
                          <p className="text-2xl font-bold text-emerald-800">${wallet.available_balance?.toFixed(2)}</p>
                        </div>
                        
                        <div>
                          <Label>Withdrawal Amount ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="1"
                            max={wallet.available_balance}
                            placeholder="Enter amount"
                            value={withdrawForm.amount}
                            onChange={(e) => setWithdrawForm({...withdrawForm, amount: e.target.value})}
                            required
                            data-testid="withdraw-amount"
                          />
                        </div>
                        
                        <div className="border-t pt-4">
                          <p className="text-sm font-medium mb-3">Bank Details</p>
                          <div className="space-y-3">
                            <div>
                              <Label>Bank Name</Label>
                              <Input
                                placeholder="e.g., Chase Bank"
                                value={withdrawForm.bank_name}
                                onChange={(e) => setWithdrawForm({...withdrawForm, bank_name: e.target.value})}
                                required
                                data-testid="bank-name"
                              />
                            </div>
                            <div>
                              <Label>Account Holder Name</Label>
                              <Input
                                placeholder="Full name on account"
                                value={withdrawForm.account_holder_name}
                                onChange={(e) => setWithdrawForm({...withdrawForm, account_holder_name: e.target.value})}
                                required
                                data-testid="account-holder"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label>Account Number</Label>
                                <Input
                                  placeholder="Account #"
                                  value={withdrawForm.account_number}
                                  onChange={(e) => setWithdrawForm({...withdrawForm, account_number: e.target.value})}
                                  required
                                  data-testid="account-number"
                                />
                              </div>
                              <div>
                                <Label>Routing Number</Label>
                                <Input
                                  placeholder="Routing #"
                                  value={withdrawForm.routing_number}
                                  onChange={(e) => setWithdrawForm({...withdrawForm, routing_number: e.target.value})}
                                  required
                                  data-testid="routing-number"
                                />
                              </div>
                            </div>
                            <div>
                              <Label>Notes (Optional)</Label>
                              <Input
                                placeholder="Any additional notes"
                                value={withdrawForm.notes}
                                onChange={(e) => setWithdrawForm({...withdrawForm, notes: e.target.value})}
                              />
                            </div>
                          </div>
                        </div>
                        
                        <Button 
                          type="submit" 
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          disabled={submittingWithdrawal}
                          data-testid="submit-withdrawal"
                        >
                          {submittingWithdrawal ? "Submitting..." : "Request Withdrawal"}
                        </Button>
                        
                        <p className="text-xs text-center text-muted-foreground">
                          Withdrawals are typically processed within 2-3 business days.
                        </p>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              
              {/* Pending Withdrawals */}
              {withdrawals.filter(w => w.status === 'pending' || w.status === 'approved').length > 0 && (
                <div className="mt-4 pt-4 border-t border-emerald-200">
                  <p className="text-sm font-medium text-emerald-700 mb-2">Pending Withdrawals</p>
                  <div className="space-y-2">
                    {withdrawals.filter(w => w.status === 'pending' || w.status === 'approved').map((w) => (
                      <div key={w.withdrawal_id} className="flex items-center justify-between bg-white/80 p-3 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">${w.amount?.toFixed(2)} → {w.bank_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(w.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className={getWithdrawalStatusBadge(w.status)}>{w.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stripe Connect Payout Setup */}
              <div className="mt-4 pt-4 border-t border-emerald-200">
                {connectStatus?.payouts_enabled ? (
                  <div className="flex items-center gap-3 bg-white/80 p-3 rounded-lg" data-testid="connect-status-ready">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">Direct Bank Payouts Enabled</p>
                      <p className="text-xs text-emerald-600">Claim payments will be sent directly to your bank account via Stripe.</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white/80 p-4 rounded-lg" data-testid="connect-setup-banner">
                    <div className="flex items-start gap-3">
                      <Banknote className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-emerald-800">Set Up Direct Bank Payouts</p>
                        <p className="text-xs text-emerald-600 mt-1">
                          Connect your bank account to receive claim payments directly. Fast, secure, and powered by Stripe.
                        </p>
                        <Button
                          size="sm"
                          className="mt-3 bg-emerald-600 hover:bg-emerald-700"
                          onClick={handleConnectOnboarding}
                          disabled={connectLoading}
                          data-testid="setup-payout-btn"
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                          {connectLoading ? "Opening Stripe..." : connectStatus?.connected && !connectStatus?.payouts_enabled ? "Complete Setup" : "Set Up Now"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loyalty Reward Progress */}
        {loyaltyStatus && loyaltyStatus.policies?.length > 0 && (
          <Card className="card-organic mb-8 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200" data-testid="loyalty-section">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <Gift className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-purple-800" style={{ fontFamily: 'Young Serif, serif' }}>
                    Loyalty Reward Program
                  </h3>
                  <p className="text-sm text-purple-600">
                    Stay claim-free for 12 months and get 15% back!
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                {loyaltyStatus.policies.map((policy) => (
                  <div key={policy.policy_id} className="bg-white/80 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm text-purple-800">
                        Policy: {policy.policy_id}
                      </span>
                      {policy.reward_already_issued ? (
                        <Badge className="bg-green-100 text-green-800">Reward Received! 🎉</Badge>
                      ) : policy.has_claim ? (
                        <Badge className="bg-gray-100 text-gray-600">Claim Filed</Badge>
                      ) : policy.is_eligible ? (
                        <Badge className="bg-purple-100 text-purple-800">Eligible! 🎁</Badge>
                      ) : (
                        <Badge className="bg-purple-50 text-purple-600">
                          {policy.months_remaining} months to go
                        </Badge>
                      )}
                    </div>
                    
                    {!policy.reward_already_issued && !policy.has_claim && (
                      <>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-purple-600">Progress</span>
                          <span className="font-medium text-purple-800">
                            {policy.months_paid} / {policy.months_required} months
                          </span>
                        </div>
                        <Progress 
                          value={(policy.months_paid / policy.months_required) * 100} 
                          className="h-2 bg-purple-100"
                        />
                        <p className="text-xs text-purple-600 mt-2">
                          Potential reward: <span className="font-semibold text-purple-800">${policy.potential_reward?.toFixed(2)}</span>
                        </p>
                      </>
                    )}
                    
                    {policy.existing_reward && (
                      <div className="mt-2 p-2 bg-green-50 rounded text-center">
                        <p className="text-sm text-green-700">
                          You received <span className="font-bold">${policy.existing_reward.reward_amount?.toFixed(2)}</span> on{' '}
                          {new Date(policy.existing_reward.created_at).toLocaleDateString()}!
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment History Section */}
        <Card className="card-organic mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200" data-testid="payment-history-section">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-800" style={{ fontFamily: 'Young Serif, serif' }}>
                    Payment History
                  </h3>
                  <p className="text-sm text-blue-600">
                    Track your premium payments
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Dialog open={paymentHistoryOpen} onOpenChange={setPaymentHistoryOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="view-payment-history-btn">
                      <Eye className="h-4 w-4 mr-2" />
                      View History
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Young Serif, serif' }} className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-blue-600" />
                        Payment History
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      {paymentHistory.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No payment history yet</p>
                          <p className="text-sm mt-2">Make your first premium payment to see it here</p>
                        </div>
                      ) : (
                        <>
                          {/* Summary */}
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="bg-blue-50 p-4 rounded-lg text-center">
                              <p className="text-2xl font-bold text-blue-700">
                                ${paymentHistory.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                              </p>
                              <p className="text-sm text-blue-600">Total Paid</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg text-center">
                              <p className="text-2xl font-bold text-green-700">
                                {paymentHistory.reduce((sum, p) => sum + (p.months_covered || 0), 0)}
                              </p>
                              <p className="text-sm text-green-600">Months Covered</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg text-center">
                              <p className="text-2xl font-bold text-purple-700">
                                {paymentHistory.length}
                              </p>
                              <p className="text-sm text-purple-600">Transactions</p>
                            </div>
                          </div>
                          
                          {/* Payment List */}
                          <div className="space-y-3">
                            {paymentHistory.map((payment, idx) => (
                              <div key={payment.payment_id || idx} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="bg-green-100 p-2 rounded-lg">
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">Premium Payment</p>
                                    <p className="text-xs text-muted-foreground">
                                      {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : 
                                       payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}
                                      {' • '}{payment.months_covered || 1} month{(payment.months_covered || 1) > 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-green-600">${(payment.amount || 0).toFixed(2)}</p>
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    {payment.status || 'completed'}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                
                <Button 
                  onClick={handleExportPaymentHistoryPdf}
                  disabled={exportingPdf}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="export-payment-history-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exportingPdf ? "Exporting..." : "Export PDF"}
                </Button>
              </div>
            </div>

            {/* Quick Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/70 p-3 rounded-lg text-center">
                <p className="text-lg font-bold text-blue-700">
                  ${paymentHistory.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground">Total Paid</p>
              </div>
              <div className="bg-white/70 p-3 rounded-lg text-center">
                <p className="text-lg font-bold text-green-700">
                  {paymentHistory.reduce((sum, p) => sum + (p.months_covered || 0), 0)}
                </p>
                <p className="text-xs text-muted-foreground">Months</p>
              </div>
              <div className="bg-white/70 p-3 rounded-lg text-center">
                <p className="text-lg font-bold text-purple-700">
                  {activePolicy?.next_billing_date ? new Date(activePolicy.next_billing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground">Next Due</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Active Policy Section */}
          <div id="policy-section">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
                Your Coverage
              </h2>
              {!activePolicy && (
                <Link to="/quote">
                  <Button size="sm" className="btn-primary" data-testid="get-quote-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Get Quote
                  </Button>
                </Link>
              )}
            </div>

            {activePolicy ? (
              <Card className="card-organic">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Policy Number</p>
                      <p className="font-mono text-sm">{activePolicy.policy_id}</p>
                    </div>
                    <Badge className={getStatusBadge(activePolicy.status)}>
                      {activePolicy.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Monthly Rent</p>
                      <p className="text-lg font-semibold">${activePolicy.monthly_rent}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Monthly Premium</p>
                      <p className="text-lg font-semibold">${Number(activePolicy.monthly_premium).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Auto-Pay Subscription Section */}
                  {activePolicy.status === "active" && (
                    (() => {
                      const policySubscription = activeSubscriptions.find(s => s.policy_id === activePolicy.policy_id);
                      const hasFirstPayment = activePolicy.first_payment_completed || paymentHistory.length > 0;
                      
                      return policySubscription ? (
                        <div className="rounded-lg p-4 mb-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                <span className="font-medium text-green-700">Auto-Pay Active</span>
                                {policySubscription.status === "canceling" && (
                                  <Badge variant="outline" className="text-yellow-600 border-yellow-400">Canceling</Badge>
                                )}
                              </div>
                              <p className="text-xs text-green-600">
                                ${policySubscription.amount?.toFixed(2)}/month • Next charge: {policySubscription.current_period_end ? new Date(policySubscription.current_period_end).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                            {policySubscription.status === "active" && (
                              <Button 
                                variant="outline"
                                onClick={() => handleCancelSubscription(policySubscription.subscription_id)}
                                className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                                data-testid="cancel-subscription-btn"
                              >
                                Cancel Auto-Pay
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className={`rounded-lg p-4 mb-4 border-2 ${hasFirstPayment ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-400 ring-2 ring-green-200' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <RefreshCw className={`h-4 w-4 ${hasFirstPayment ? 'text-green-600' : 'text-blue-600'}`} />
                                <span className={`font-medium ${hasFirstPayment ? 'text-green-700' : 'text-blue-700'}`}>
                                  Auto-Pay {hasFirstPayment ? '(Recommended)' : ''}
                                </span>
                                {hasFirstPayment && (
                                  <Badge className="bg-green-500 text-white text-xs">Recommended</Badge>
                                )}
                              </div>
                              <p className={`text-xs ${hasFirstPayment ? 'text-green-600' : 'text-blue-600'}`}>
                                {hasFirstPayment 
                                  ? '✨ Set up now to ensure continuous coverage and never miss a payment!'
                                  : 'Set up automatic monthly payments and never miss a due date'}
                              </p>
                            </div>
                            <Button 
                              onClick={() => handleSetupSubscription(activePolicy.policy_id)}
                              disabled={creatingSubscription}
                              className={hasFirstPayment ? 'bg-green-600 hover:bg-green-700 animate-pulse' : 'bg-blue-600 hover:bg-blue-700'}
                              data-testid="setup-autopay-btn"
                            >
                              {creatingSubscription ? (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  Setting up...
                                </>
                              ) : (
                                <>
                                  <CreditCard className="h-4 w-4 mr-2" />
                                  Set Up Auto-Pay
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })()
                  )}

                  {/* Payment Options - Always visible for active policies */}
                  {activePolicy.status === "active" && (
                    <div className="rounded-lg p-4 mb-4 bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <DollarSign className="h-4 w-4 text-primary" />
                            <span className="font-medium text-primary">One-Time Payment</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Pay anytime - pay ahead to unlock claim eligibility faster
                          </p>
                        </div>
                        <Button 
                          onClick={() => handlePayPremium(activePolicy.policy_id)}
                          data-testid="make-payment-btn"
                          className="bg-primary hover:bg-primary/90"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Make a Payment
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Billing Status - Due indicator */}
                  {activePolicy.next_billing_date && isPremiumDue(activePolicy) && (
                    <div className="rounded-lg p-3 mb-4 bg-red-50 border border-red-200">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <span className="text-sm font-medium text-red-800">Payment Overdue</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3 text-red-500" />
                          <span className="text-red-700">
                            Due: {new Date(activePolicy.next_billing_date).toLocaleDateString()}
                          </span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handlePayPremium(activePolicy.policy_id)}
                          data-testid="pay-premium-due-btn"
                        >
                          Pay Now ${Number(activePolicy.monthly_premium).toFixed(2)}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Next billing info - when not due */}
                  {activePolicy.next_billing_date && !isPremiumDue(activePolicy) && (
                    <div className="rounded-lg p-3 mb-4 bg-muted/50 border">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Next scheduled payment: {new Date(activePolicy.next_billing_date).toLocaleDateString()}
                          {getDaysUntilBilling(activePolicy) && (
                            <span className="ml-1">({getDaysUntilBilling(activePolicy)} days)</span>
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-border pt-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Coverage Period</span>
                      <span className="font-medium">
                        {activePolicy.coverage_start_date && !isNaN(new Date(activePolicy.coverage_start_date).getTime()) 
                          ? new Date(activePolicy.coverage_start_date).toLocaleDateString() 
                          : activePolicy.start_date && !isNaN(new Date(activePolicy.start_date).getTime())
                            ? new Date(activePolicy.start_date).toLocaleDateString()
                            : 'N/A'} - {activePolicy.coverage_end_date && !isNaN(new Date(activePolicy.coverage_end_date).getTime()) 
                          ? new Date(activePolicy.coverage_end_date).toLocaleDateString() 
                          : activePolicy.end_date && !isNaN(new Date(activePolicy.end_date).getTime())
                            ? new Date(activePolicy.end_date).toLocaleDateString()
                            : 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => handleDownloadContract(activePolicy.policy_id)}
                      data-testid="download-contract-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Contract
                    </Button>
                    <Link to="/file-claim" className="flex-1">
                      <Button className="w-full" variant="outline" data-testid="file-claim-btn">
                        File a Claim
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="card-organic">
                <CardContent className="p-8 text-center">
                  <div className="bg-muted rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <NestIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">No Active Coverage</h3>
                  <p className="text-muted-foreground mb-4">
                    Get a quote to start protecting your rent today.
                  </p>
                  <Link to="/quote">
                    <Button className="btn-primary" data-testid="start-quote-btn">Get Your Quote</Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Quotes Section */}
          <div id="quotes-section">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
                Your Quotes
              </h2>
            </div>

            {data.quotes.length > 0 ? (
              <div className="space-y-4">
                {data.quotes.slice(0, 3).map((quote) => (
                  <Card key={quote.quote_id} className="dashboard-card">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-mono text-xs text-muted-foreground">{quote.quote_id}</p>
                          <p className="font-semibold">${quote.monthly_rent}/month rent</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusBadge(quote.status)}>
                            {quote.status}
                          </Badge>
                          {quote.status !== "converted" && (
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => handleEditQuote(quote)}
                                data-testid={`edit-quote-${quote.quote_id}`}
                              >
                                <Edit2 className="h-4 w-4 text-muted-foreground hover:text-primary" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8"
                                onClick={() => setDeleteConfirm(quote.quote_id)}
                                data-testid={`delete-quote-${quote.quote_id}`}
                              >
                                <Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-500" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">
                          Premium: ${Number(quote.monthly_premium).toFixed(2)}/mo
                        </span>
                        {(quote.status === "pending" || quote.status === "approved") && (
                          <Button 
                            size="sm" 
                            onClick={() => handlePayQuote(quote.quote_id)}
                            data-testid={`pay-quote-${quote.quote_id}`}
                          >
                            Pay ${Number(quote.total_first_payment).toFixed(2)}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="card-organic">
                <CardContent className="p-8 text-center">
                  <p className="text-muted-foreground">No quotes yet</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Edit Quote Dialog */}
        <Dialog open={!!editingQuote} onOpenChange={(open) => !open && setEditingQuote(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>Edit Quote</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Monthly Rent ($)</Label>
                <Input
                  type="number"
                  value={editForm.monthly_rent}
                  onChange={(e) => setEditForm({...editForm, monthly_rent: e.target.value})}
                  data-testid="edit-quote-rent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Lease Start</Label>
                  <Input
                    type="date"
                    value={editForm.lease_start_date}
                    onChange={(e) => setEditForm({...editForm, lease_start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Lease End</Label>
                  <Input
                    type="date"
                    value={editForm.lease_end_date}
                    onChange={(e) => setEditForm({...editForm, lease_end_date: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>Employer Name</Label>
                <Input
                  value={editForm.employer_name}
                  onChange={(e) => setEditForm({...editForm, employer_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Employment Start Date</Label>
                <Input
                  type="date"
                  value={editForm.employment_start_date}
                  onChange={(e) => setEditForm({...editForm, employment_start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Annual Income ($)</Label>
                <Input
                  type="number"
                  value={editForm.annual_income}
                  onChange={(e) => setEditForm({...editForm, annual_income: e.target.value})}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveQuote} disabled={savingQuote} className="flex-1" data-testid="save-quote-btn">
                  {savingQuote ? "Saving..." : "Save Changes"}
                </Button>
                <Button variant="outline" onClick={() => setEditingQuote(null)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>Delete Quote?</DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Are you sure you want to delete this quote? This action cannot be undone.
            </p>
            <div className="flex gap-2 mt-4">
              <Button 
                variant="destructive" 
                onClick={() => handleDeleteQuote(deleteConfirm)} 
                className="flex-1"
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Claims Section */}
        {data.claims.length > 0 && (
          <div className="mt-8" id="claims-section">
            <h2 className="text-xl font-bold text-foreground mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
              Your Claims
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.claims.map((claim) => (
                <Card key={claim.claim_id} className="dashboard-card cursor-pointer" onClick={() => navigate(`/claims/${claim.claim_id}`)}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-mono text-xs text-muted-foreground">{claim.claim_id}</p>
                        <p className="text-sm text-muted-foreground">
                          Filed: {new Date(claim.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className={getStatusBadge(claim.status)}>
                        {claim.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm">Job loss: {new Date(claim.job_loss_date).toLocaleDateString()}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm text-muted-foreground">
                        {claim.documents?.length || 0} documents
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          <Link to="/quote" className="dashboard-card p-4 text-center hover:border-primary">
            <FileText className="h-6 w-6 mx-auto mb-2 text-primary" />
            <p className="font-medium">New Quote</p>
          </Link>
          <Link to="/documents" className="dashboard-card p-4 text-center hover:border-primary">
            <FolderOpen className="h-6 w-6 mx-auto mb-2 text-primary" />
            <p className="font-medium">Documents</p>
          </Link>
          <Link to="/beneficiaries" className="dashboard-card p-4 text-center hover:border-primary">
            <Users className="h-6 w-6 mx-auto mb-2 text-primary" />
            <p className="font-medium">Beneficiaries</p>
          </Link>
          <Link to="/profile" className="dashboard-card p-4 text-center hover:border-primary">
            <User className="h-6 w-6 mx-auto mb-2 text-primary" />
            <p className="font-medium">Profile</p>
          </Link>
          <Link to="/referrals" className="dashboard-card p-4 text-center hover:border-primary bg-gradient-to-br from-primary/5 to-primary/10" data-testid="referrals-link">
            <Gift className="h-6 w-6 mx-auto mb-2 text-primary" />
            <p className="font-medium">Refer & Earn</p>
          </Link>
          <Link to="/file-claim" className="dashboard-card p-4 text-center hover:border-primary">
            <AlertCircle className="h-6 w-6 mx-auto mb-2 text-primary" />
            <p className="font-medium">File Claim</p>
          </Link>
        </div>

        {/* Flexible Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <CreditCard className="inline h-5 w-5 mr-2 text-primary" />
                Make a Payment
              </DialogTitle>
            </DialogHeader>
            
            {paymentStatus && (
              <div className="space-y-4">
                {/* Payment Status Summary */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Monthly Premium:</span>
                    <span className="font-medium">${paymentStatus.monthly_premium?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Months Paid:</span>
                    <span className="font-medium">{paymentStatus.months_paid} / {paymentStatus.months_required}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Total Paid:</span>
                    <span className="font-medium">${paymentStatus.total_paid?.toFixed(2)}</span>
                  </div>
                  <Progress value={(paymentStatus.months_paid / paymentStatus.months_required) * 100} className="h-2" />
                </div>

                {/* Eligibility Status */}
                <div className={`p-4 rounded-lg border ${paymentStatus.is_eligible ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  {paymentStatus.is_eligible ? (
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">You are eligible to file claims!</span>
                    </div>
                  ) : (
                    <div className="text-yellow-800">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-5 w-5" />
                        <span className="font-medium">Not yet eligible for claims</span>
                      </div>
                      <p className="text-sm">
                        {paymentStatus.remaining_months_for_eligibility > 0 && (
                          <span>Pay {paymentStatus.remaining_months_for_eligibility} more month(s) </span>
                        )}
                        {paymentStatus.allow_early_full_payment && paymentStatus.can_pay_full_year && (
                          <span>or pay the full annual premium to become eligible immediately</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Auto-Pay Option - Recommended */}
                {!activeSubscriptions.find(s => s.policy_id === selectedPolicyForPayment?.policy_id) && (
                  <div className="border-2 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 ring-2 ring-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <RefreshCw className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-green-700">Auto-Pay</span>
                          <Badge className="bg-green-500 text-white text-xs">Recommended</Badge>
                        </div>
                        <p className="text-xs text-green-600">
                          ${paymentStatus.monthly_premium?.toFixed(2)}/month • Never miss a payment
                        </p>
                      </div>
                      <Button 
                        onClick={() => {
                          setPaymentDialogOpen(false);
                          handleSetupSubscription(selectedPolicyForPayment?.policy_id);
                        }}
                        disabled={creatingSubscription}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {creatingSubscription ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Setting up...
                          </>
                        ) : (
                          <>
                            <CreditCard className="h-4 w-4 mr-2" />
                            Set Up Auto-Pay
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Or separator */}
                {!activeSubscriptions.find(s => s.policy_id === selectedPolicyForPayment?.policy_id) && !paymentStatus.full_premium_paid && (
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-muted-foreground/20"></div>
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-background px-3 text-sm text-muted-foreground">or make a one-time payment</span>
                    </div>
                  </div>
                )}

                {/* Payment Options */}
                {!paymentStatus.full_premium_paid && (
                  <div className="space-y-4">
                    {/* Pay Monthly */}
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium mb-3">Pay by Month</h4>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <Label>Number of Months</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              type="number"
                              min="1"
                              max="12"
                              value={monthsToPay}
                              onChange={(e) => setMonthsToPay(Math.max(1, Math.min(12, parseInt(e.target.value) || 1)))}
                              className="w-20"
                            />
                            <span className="text-sm text-muted-foreground">
                              = ${(monthsToPay * (paymentStatus.monthly_premium || 0)).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <Button onClick={handleFlexiblePayment} disabled={processingPayment}>
                          {processingPayment ? "Processing..." : `Pay ${monthsToPay} Month${monthsToPay > 1 ? 's' : ''}`}
                        </Button>
                      </div>
                    </div>

                    {/* Pay Full Year */}
                    {paymentStatus.allow_early_full_payment && paymentStatus.can_pay_full_year && (
                      <div className="border-2 border-primary/30 bg-primary/5 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              <Gift className="h-4 w-4 text-primary" />
                              Pay Full Year & Unlock Claims
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              Become eligible immediately
                              {paymentStatus.discount_percent > 0 && (
                                <Badge variant="secondary" className="ml-2">{paymentStatus.discount_percent}% OFF</Badge>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-bold text-primary">
                              ${paymentStatus.full_year_payment_amount?.toFixed(2)}
                            </p>
                            {paymentStatus.discount_amount > 0 && (
                              <p className="text-xs text-muted-foreground line-through">
                                ${paymentStatus.remaining_for_full_year?.toFixed(2)}
                              </p>
                            )}
                          </div>
                          <Button onClick={handlePayFullYear} disabled={processingPayment} className="bg-primary">
                            {processingPayment ? "Processing..." : "Pay Full Year"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {paymentStatus.full_premium_paid && (
                  <div className="text-center py-4 text-green-600">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-2" />
                    <p className="font-medium">Full annual premium has been paid!</p>
                    <p className="text-sm">You have full claim eligibility.</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
